import {
  authLogin,
  authPasswordComplete,
  authPasswordResend,
  authPasswordStart,
  authPasswordVerify,
  authRefresh,
  authResend2fa,
  authTokensSchema,
  authVerify2fa,
  loginChallengeSchema,
  loginRequestSchema,
  passwordCompleteRequestSchema,
  passwordStartRequestSchema,
  problemDetailsSchema,
  refreshHeadersSchema,
  resendLogin2faRequestSchema,
  verifyLogin2faRequestSchema,
  type AuthTokens,
  type LoginChallenge,
} from '@hf/sharedContract';
import { apiUrl } from '../http/apiUrl';
import { getMfaTrustToken } from './session';

export type AuthApiSuccess = { ok: true; tokens: AuthTokens };
export type AuthChallengeSuccess = { ok: true; challenge: LoginChallenge };
export type AuthAckSuccess = { ok: true };
export type AuthApiAborted = { ok: false; aborted: true };
export type AuthApiFailure = { ok: false; aborted?: false; status: number; message: string };
export type AuthApiResult = AuthApiSuccess | AuthApiAborted | AuthApiFailure;
export type AuthChallengeResult = AuthChallengeSuccess | AuthApiAborted | AuthApiFailure;
export type AuthAckResult = AuthAckSuccess | AuthApiAborted | AuthApiFailure;
/** Password login may return tokens (trusted MFA) or an email challenge. */
export type AuthLoginResult = AuthApiSuccess | AuthChallengeSuccess | AuthApiAborted | AuthApiFailure;

let loginController: AbortController | null = null;
let verifyController: AbortController | null = null;
let resendController: AbortController | null = null;
let passwordStartController: AbortController | null = null;
let passwordResendController: AbortController | null = null;
let passwordVerifyController: AbortController | null = null;
let passwordCompleteController: AbortController | null = null;
let refreshInFlight: Promise<AuthApiResult> | null = null;

export function resetAuthClient(): void {
  loginController?.abort();
  loginController = null;
  verifyController?.abort();
  verifyController = null;
  resendController?.abort();
  resendController = null;
  passwordStartController?.abort();
  passwordStartController = null;
  passwordResendController?.abort();
  passwordResendController = null;
  passwordVerifyController?.abort();
  passwordVerifyController = null;
  passwordCompleteController?.abort();
  passwordCompleteController = null;
  refreshInFlight = null;
}

function fallbackMessage(status: number): string {
  if (status === 401) return 'Invalid email or password';
  if (status === 400) return 'Invalid request';
  if (status === 404) return 'Not found';
  if (status === 429) return 'Too many code requests. Try again later.';
  if (status === 502) return 'Service unavailable';
  return 'Unable to reach the server';
}

function messageFromBody(data: unknown, status: number): string {
  const parsed = problemDetailsSchema.safeParse(data);
  if (parsed.success) return parsed.data.detail ?? parsed.data.title;
  return fallbackMessage(status);
}

async function readJson(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  return response.json().catch(() => null);
}

function abortedResult(): AuthApiAborted {
  return { ok: false, aborted: true };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException ? error.name === 'AbortError' : error instanceof Error && error.name === 'AbortError';
}

export type LoginPasswordOptions = { datasource?: string; useDatasource: boolean };

/** Password step — MFA challenge, or session tokens when MFA trust is still valid. */
export async function loginWithPassword(email: string, password: string, options: LoginPasswordOptions): Promise<AuthLoginResult> {
  const mfaTrustToken = getMfaTrustToken();
  const parsedRequest = loginRequestSchema.safeParse({
    email,
    password,
    useDatasource: options.useDatasource,
    ...(options.useDatasource ? { datasource: (options.datasource ?? '').trim().toLowerCase() } : {}),
    ...(mfaTrustToken ? { mfaTrustToken } : {}),
  });
  if (!parsedRequest.success) {
    return { ok: false, status: 400, message: parsedRequest.error.issues[0]?.message ?? 'Invalid request' };
  }

  loginController?.abort();
  const controller = new AbortController();
  loginController = controller;

  try {
    const response = await fetch(apiUrl(authLogin.path), {
      method: authLogin.method.toUpperCase(),
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(parsedRequest.data),
    });
    const body: unknown = await readJson(response);
    if (response.status !== 200) {
      return { ok: false, status: response.status, message: messageFromBody(body, response.status) };
    }
    const parsedTokens = authTokensSchema.safeParse(body);
    if (parsedTokens.success) {
      return { ok: true, tokens: parsedTokens.data };
    }
    const parsedChallenge = loginChallengeSchema.safeParse(body);
    if (parsedChallenge.success) {
      return { ok: true, challenge: parsedChallenge.data };
    }
    return { ok: false, status: 502, message: 'Service unavailable' };
  } catch (error) {
    if (controller.signal.aborted || isAbortError(error)) return abortedResult();
    return { ok: false, status: 0, message: 'Unable to reach the server' };
  } finally {
    if (loginController === controller) loginController = null;
  }
}

export async function verifyLogin2fa(challengeToken: string, code: string): Promise<AuthApiResult> {
  const parsedRequest = verifyLogin2faRequestSchema.safeParse({ challengeToken, code });
  if (!parsedRequest.success) {
    return { ok: false, status: 400, message: parsedRequest.error.issues[0]?.message ?? 'Invalid request' };
  }

  verifyController?.abort();
  const controller = new AbortController();
  verifyController = controller;

  try {
    const response = await fetch(apiUrl(authVerify2fa.path), {
      method: authVerify2fa.method.toUpperCase(),
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(parsedRequest.data),
    });
    const body: unknown = await readJson(response);
    if (response.status !== 200) {
      const message = response.status === 401 ? 'Invalid or expired code' : messageFromBody(body, response.status);
      return { ok: false, status: response.status, message };
    }
    const parsedTokens = authTokensSchema.safeParse(body);
    if (!parsedTokens.success) {
      return { ok: false, status: 502, message: 'Service unavailable' };
    }
    const tokens: AuthTokens = parsedTokens.data;
    return { ok: true, tokens };
  } catch (error) {
    if (controller.signal.aborted || isAbortError(error)) return abortedResult();
    return { ok: false, status: 0, message: 'Unable to reach the server' };
  } finally {
    if (verifyController === controller) verifyController = null;
  }
}

export async function resendLogin2fa(challengeToken: string): Promise<AuthChallengeResult> {
  const parsedRequest = resendLogin2faRequestSchema.safeParse({ challengeToken });
  if (!parsedRequest.success) {
    return { ok: false, status: 400, message: parsedRequest.error.issues[0]?.message ?? 'Invalid request' };
  }

  resendController?.abort();
  const controller = new AbortController();
  resendController = controller;

  try {
    const response = await fetch(apiUrl(authResend2fa.path), {
      method: authResend2fa.method.toUpperCase(),
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(parsedRequest.data),
    });
    const body: unknown = await readJson(response);
    if (response.status !== 200) {
      return { ok: false, status: response.status, message: messageFromBody(body, response.status) };
    }
    const parsedChallenge = loginChallengeSchema.safeParse(body);
    if (!parsedChallenge.success) {
      return { ok: false, status: 502, message: 'Service unavailable' };
    }
    const challenge: LoginChallenge = parsedChallenge.data;
    return { ok: true, challenge };
  } catch (error) {
    if (controller.signal.aborted || isAbortError(error)) return abortedResult();
    return { ok: false, status: 0, message: 'Unable to reach the server' };
  } finally {
    if (resendController === controller) resendController = null;
  }
}

async function refreshOnce(refreshToken: string, expiredAccessToken: string): Promise<AuthApiResult> {
  const parsedHeaders = refreshHeadersSchema.safeParse({
    authorization: `Bearer ${refreshToken}`,
    'x-expired-token': expiredAccessToken,
  });
  if (!parsedHeaders.success) {
    return { ok: false, status: 400, message: 'Invalid request' };
  }

  try {
    const response = await fetch(apiUrl(authRefresh.path), {
      method: authRefresh.method.toUpperCase(),
      headers: {
        Authorization: parsedHeaders.data.authorization,
        'X-Expired-Token': parsedHeaders.data['x-expired-token'],
      },
    });
    const body: unknown = await readJson(response);
    if (response.status !== 200) {
      const message = response.status === 401 ? 'Session expired' : messageFromBody(body, response.status);
      return { ok: false, status: response.status, message };
    }
    const parsedTokens = authTokensSchema.safeParse(body);
    if (!parsedTokens.success) {
      return { ok: false, status: 502, message: 'Service unavailable' };
    }
    const tokens: AuthTokens = parsedTokens.data;
    return { ok: true, tokens };
  } catch (error) {
    if (isAbortError(error)) return abortedResult();
    return { ok: false, status: 0, message: 'Unable to reach the server' };
  }
}

export async function startPasswordSetup(email: string): Promise<AuthChallengeResult> {
  const parsedRequest = passwordStartRequestSchema.safeParse({ email });
  if (!parsedRequest.success) {
    return { ok: false, status: 400, message: parsedRequest.error.issues[0]?.message ?? 'Invalid request' };
  }

  passwordStartController?.abort();
  const controller = new AbortController();
  passwordStartController = controller;

  try {
    const response = await fetch(apiUrl(authPasswordStart.path), {
      method: authPasswordStart.method.toUpperCase(),
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(parsedRequest.data),
    });
    const body: unknown = await readJson(response);
    if (response.status !== 200) {
      return { ok: false, status: response.status, message: messageFromBody(body, response.status) };
    }
    const parsedChallenge = loginChallengeSchema.safeParse(body);
    if (!parsedChallenge.success) {
      return { ok: false, status: 502, message: 'Service unavailable' };
    }
    return { ok: true, challenge: parsedChallenge.data };
  } catch (error) {
    if (controller.signal.aborted || isAbortError(error)) return abortedResult();
    return { ok: false, status: 0, message: 'Unable to reach the server' };
  } finally {
    if (passwordStartController === controller) passwordStartController = null;
  }
}

export async function resendPasswordSetup(challengeToken: string): Promise<AuthChallengeResult> {
  const parsedRequest = resendLogin2faRequestSchema.safeParse({ challengeToken });
  if (!parsedRequest.success) {
    return { ok: false, status: 400, message: parsedRequest.error.issues[0]?.message ?? 'Invalid request' };
  }

  passwordResendController?.abort();
  const controller = new AbortController();
  passwordResendController = controller;

  try {
    const response = await fetch(apiUrl(authPasswordResend.path), {
      method: authPasswordResend.method.toUpperCase(),
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(parsedRequest.data),
    });
    const body: unknown = await readJson(response);
    if (response.status !== 200) {
      return { ok: false, status: response.status, message: messageFromBody(body, response.status) };
    }
    const parsedChallenge = loginChallengeSchema.safeParse(body);
    if (!parsedChallenge.success) {
      return { ok: false, status: 502, message: 'Service unavailable' };
    }
    return { ok: true, challenge: parsedChallenge.data };
  } catch (error) {
    if (controller.signal.aborted || isAbortError(error)) return abortedResult();
    return { ok: false, status: 0, message: 'Unable to reach the server' };
  } finally {
    if (passwordResendController === controller) passwordResendController = null;
  }
}

export async function verifyPasswordSetup(challengeToken: string, code: string): Promise<AuthAckResult> {
  const parsedRequest = verifyLogin2faRequestSchema.safeParse({ challengeToken, code });
  if (!parsedRequest.success) {
    return { ok: false, status: 400, message: parsedRequest.error.issues[0]?.message ?? 'Invalid request' };
  }

  passwordVerifyController?.abort();
  const controller = new AbortController();
  passwordVerifyController = controller;

  try {
    const response = await fetch(apiUrl(authPasswordVerify.path), {
      method: authPasswordVerify.method.toUpperCase(),
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(parsedRequest.data),
    });
    const body: unknown = await readJson(response);
    if (response.status === 204) {
      return { ok: true };
    }
    if (response.status !== 200) {
      const message = response.status === 401 ? 'Invalid or expired code' : messageFromBody(body, response.status);
      return { ok: false, status: response.status, message };
    }
    return { ok: true };
  } catch (error) {
    if (controller.signal.aborted || isAbortError(error)) return abortedResult();
    return { ok: false, status: 0, message: 'Unable to reach the server' };
  } finally {
    if (passwordVerifyController === controller) passwordVerifyController = null;
  }
}

export async function completePasswordSetup(challengeToken: string, code: string, newPassword: string): Promise<AuthApiResult> {
  const parsedRequest = passwordCompleteRequestSchema.safeParse({ challengeToken, code, newPassword });
  if (!parsedRequest.success) {
    return { ok: false, status: 400, message: parsedRequest.error.issues[0]?.message ?? 'Invalid request' };
  }

  passwordCompleteController?.abort();
  const controller = new AbortController();
  passwordCompleteController = controller;

  try {
    const response = await fetch(apiUrl(authPasswordComplete.path), {
      method: authPasswordComplete.method.toUpperCase(),
      signal: controller.signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(parsedRequest.data),
    });
    const body: unknown = await readJson(response);
    if (response.status !== 200) {
      const message = response.status === 401 ? 'Invalid or expired code' : messageFromBody(body, response.status);
      return { ok: false, status: response.status, message };
    }
    const parsedTokens = authTokensSchema.safeParse(body);
    if (!parsedTokens.success) {
      return { ok: false, status: 502, message: 'Service unavailable' };
    }
    return { ok: true, tokens: parsedTokens.data };
  } catch (error) {
    if (controller.signal.aborted || isAbortError(error)) return abortedResult();
    return { ok: false, status: 0, message: 'Unable to reach the server' };
  } finally {
    if (passwordCompleteController === controller) passwordCompleteController = null;
  }
}

/** One in-flight refresh at a time — refresh JWTs rotate, so parallel calls would log the user out. */
export function refreshWithTokens(refreshToken: string, expiredAccessToken: string): Promise<AuthApiResult> {
  if (!refreshInFlight) {
    refreshInFlight = refreshOnce(refreshToken, expiredAccessToken).finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}
